import Echo, { type Broadcaster } from 'laravel-echo'
import Pusher from 'pusher-js'

import { useAdminRoutes } from '@shared/lib/routes'

type PusherPrivateChannel = ReturnType<Echo<'pusher'>['private']>

/** Pusher's custom channel authorization contract. */
interface ChannelAuthorizationRequestParams {
  socketId: string
  channelName: string
}

type ChannelAuthorizationCallback = (error: Error | null, data: { auth: string } | null) => void

export type { PusherPrivateChannel }

let echoInstance: Echo<keyof Broadcaster> | null = null

/**
 * One Echo client for the app. Private channels are authorized through the app's axios instance, so the call
 * carries the current admin JWT and is refreshed with it — a token fixed at connect time would expire in an hour.
 */
const getEcho = (): Echo<keyof Broadcaster> => {
  if (echoInstance) return echoInstance

  const config = useRuntimeConfig()
  const { $axios } = useNuxtApp()

  const pusherPort = Number(config.public.pusherPort)
  const pusherHost = config.public.pusherHost
  const pusherTls: unknown = config.public.pusherTls
  const forceTLS = pusherTls === true || pusherTls === 'true'
  const authUrl = useAdminRoutes('admin.broadcasting.auth') ?? ''

  // @ts-expect-error laravel-echo reads the Pusher class from window
  window.Pusher = Pusher

  echoInstance = new Echo({
    broadcaster: 'pusher',
    key: config.public.pusherKey,
    wsHost: pusherHost,
    wsPort: pusherPort,
    wssHost: pusherHost,
    wssPort: pusherPort,
    forceTLS,
    cluster: 'mt1',
    enabledTransports: ['ws', 'wss'],
    channelAuthorization: {
      customHandler: (params: ChannelAuthorizationRequestParams, callback: ChannelAuthorizationCallback): void => {
        $axios.post(authUrl, { socket_id: params.socketId, channel_name: params.channelName })
          .then(({ data }) => callback(null, data))
          .catch((error: Error) => callback(error, null))
      },
    },
  } as ConstructorParameters<typeof Echo<'pusher'>>[0])

  return echoInstance
}

/** Joins a private channel; Echo adds the `private-` prefix. */
export const useChannel = (name: string): PusherPrivateChannel => getEcho().private(name) as PusherPrivateChannel

/** Custom `broadcastAs` events need the leading dot, e.g. `.discipline.schedule.allocation.done`. */
export const listenChannel = (channel: PusherPrivateChannel, event: string, callback: (data: unknown) => void): void => {
  channel.listen(event, callback)
}

export const stopListeningEvent = (channel: PusherPrivateChannel, event: string, callback: (data: unknown) => void): void => {
  channel.stopListening(event, callback)
}

export const leaveChannel = (name: string): void => {
  echoInstance?.leave(name)
}
